import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DeploymentContext } from '../../../utilities/deployment-context/deployment-context.js';
import {
	Mode,
	ReferenceValuesBySector,
	SectorWithChildOptions,
} from 'company-finder-common';
import { WebAnalyticsService } from '../../../services/web-analytics/web.analytics.js';

@Component({
	selector: 'sector-and-child-dropdown',
	templateUrl: './sector-and-child-dropdown.component.html',
	styleUrls: ['../base-selector.scss'],
	standalone: false,
})
export class SectorAndChildDropdownComponent {
	@Input()
	label = 'Sector & Sub-Sector';

	@Input()
	public isPrimary = true;

	@Input()
	public otherField: 'subsector' | 'developmentStage' | 'focusArea' =
		'subsector';

	@Input()
	public selectableSectors: boolean = true;

	@Input()
	public singleSector: string = null;

	public constructor(
		private context: DeploymentContext,
		private webAnalyticsService: WebAnalyticsService
	) {}

	public get sectorData(): SectorWithChildOptions[] {
		switch (this.otherField) {
			case 'subsector':
				return (
					this.context.comprehensiveSummary.allSectorsWithCounts?.map(
						(x) => ({
							sectorOrOption: x.sector,
							parentSector: x.parentSector,
							children: x.subSectors,
						})
					) ?? []
				).filter(
					(option) =>
						// If we support only a single sector, each option
						// must be that sector or a child of it
						this.isOptionVisible(option) &&
						// Assets have focus areas, which are roughly analogous to subsectors
						// but they don't have subsectors directly, so do not include them
						(this.context.currentMode === Mode.Company ||
							!option.parentSector)
				);

			case 'focusArea': {
				return (
					this.getOtherFieldOptions(
						this.context.referenceValueData?.focusAreas
					) ?? []
				);
			}
			case 'developmentStage': {
				return (
					this.getOtherFieldOptions(
						this.context.referenceValueData?.developmentStages
					) ?? []
				);
			}
		}
		return [];
	}

	private getOtherFieldOptions(field: ReferenceValuesBySector) {
		const options: SectorWithChildOptions[] = [];

		for (const sector of this.context.comprehensiveSummary.sectors) {
			options.push({
				parentSector: null,
				sectorOrOption: sector,
				children: field[sector].map((opt) => opt.value) ?? [],
			});
			const sectOpts: SectorWithChildOptions[] =
				field[sector]?.map((x) => ({
					sectorOrOption: x.value,
					parentSector: sector,
				})) ?? [];

			options.push(...sectOpts);
		}
		const filtered = options.filter((option) =>
			this.isOptionVisible(option)
		);
		return filtered;
	}

	private isOptionVisible(option: SectorWithChildOptions) {
		return (
			!this.singleSector ||
			option.sectorOrOption === this.singleSector ||
			option.parentSector === this.singleSector
		);
	}

	// Don't use the output name "select", otherwise text selection in the filter
	// control also propagates up through this with an undesired event object
	@Output() optionsSelected = new EventEmitter<SectorWithChildOptions[]>();
	public relaySelect(event: SectorWithChildOptions[]): void {
		this.webAnalyticsService.trackEvent('filter', {
			category: `Filter by sectorSubSector`,
			label: `${event}`,
		});
		this.optionsSelected.emit(event);
	}
}
